import React from 'react';
import { Button, Form, InputGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import { 
  FaAngleDoubleLeft,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleRight,
  FaList
} from 'react-icons/fa';

/**
 * Contrôles de pagination réutilisable
 */
const PaginationControls = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  startIndex,
  endIndex,
  hasPreviousPage,
  hasNextPage,
  goToPage,
  nextPage,
  previousPage,
  firstPage,
  lastPage,
  changePageSize,
  pageSizeOptions = [15, 30, 50, 100]
}) => {
  /**
   * Génère la liste des pages à afficher
   */
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Toutes les pages si peu nombreuses
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Logique d'affichage avec ellipsis
      if (currentPage <= 3) {
        // Début de la liste
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Fin de la liste
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        // Milieu de la liste
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  /**
   * Gère la saisie manuelle de page
   */
  const handlePageInput = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= totalPages) {
      goToPage(value);
    }
  };

  return (
    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
      {/* Informations sur la pagination */}
      <div className="text-muted small">
        <FaList className="me-1" />
        Affichage <strong>{startIndex}-{endIndex}</strong> sur <strong>{totalItems}</strong> historiques
        {totalItems > 0 && (
          <span className="ms-2">
            (Page <strong>{currentPage}</strong> sur <strong>{totalPages}</strong>)
          </span>
        )}
      </div>

      {/* Contrôles de pagination */}
      <div className="d-flex align-items-center gap-2">
        {/* Sélecteur de taille de page */}
        <DropdownButton
          variant="outline-primary"
          size="sm"
          title={`${pageSize} / page`}
          align="end"
        >
          {pageSizeOptions.map(size => (
            <Dropdown.Item 
              key={size}
              active={pageSize === size}
              onClick={() => changePageSize(size)}
            >
              {size} par page
            </Dropdown.Item>
          ))}
        </DropdownButton>

        {/* Boutons de navigation */}
        <div className="btn-group" role="group">
          {/* Première page */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={firstPage}
            disabled={!hasPreviousPage}
            title="Première page"
          >
            <FaAngleDoubleLeft />
          </Button>

          {/* Page précédente */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={previousPage}
            disabled={!hasPreviousPage}
            title="Page précédente"
          >
            <FaAngleLeft />
          </Button>

          {/* Pages numérotées */}
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <Button variant="outline-primary" size="sm" disabled>
                  ...
                </Button>
              ) : (
                <Button
                  variant={currentPage === page ? "primary" : "outline-primary"}
                  size="sm"
                  onClick={() => goToPage(page)}
                  active={currentPage === page}
                >
                  {page}
                </Button>
              )}
            </React.Fragment>
          ))}

          {/* Page suivante */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={nextPage}
            disabled={!hasNextPage}
            title="Page suivante"
          >
            <FaAngleRight />
          </Button>

          {/* Dernière page */}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={lastPage}
            disabled={!hasNextPage}
            title="Dernière page"
          >
            <FaAngleDoubleRight />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaginationControls;